import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Alert, Box, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RequiredFeatureEnabled } from 'src/components/auth';
import { DqNamedOffendersSection } from 'src/components/organisms/NewReportDetails/components';
import { casePageHeader } from 'src/components/pages/Case/Case.slice';
import {
  CaseType,
  CategoryDisplayType,
  CoiCaseStatus,
  MitigationPlansForDisclosureQuery,
  MitigationPlanStatus,
  ReportType,
} from 'src/graphql';
import theme from 'src/theme/theme';
import { CaseTypeEnum, FeatureFlag, REPORT_SOURCE } from 'src/types';
import { CaseFormResponses } from '../../../organisms/CaseFormResponses/CaseFormResponses';
import { CaseOverviewPanel } from '../../../organisms/CaseOverviewPanel/CaseOverviewPanel';
import { CaseSubjectDetails } from '../../../organisms/CaseSubjectDetails/CaseSubjectDetails';
import { ApprovedWithoutMitigation } from '../ApprovedWithoutMitigation/ApprovedWithoutMitigation';
import { CaseCustomFields } from '../CaseCustomFields';
import { LinkCasesSection } from '../LinkCasesSection/LinkCasesSection';

interface Props {
  caseById: CaseType;
  caseType: CaseTypeEnum;
  caseStatus: string;
  categories: CategoryDisplayType[];
  firstReport: ReportType | undefined;
  firstMitigationPlan:
    | MitigationPlansForDisclosureQuery['mitigationPlansForDisclosure'][0]
    | null;
  formTitle: string;
  submissionRoute?: string;
}

export const CaseReportTab = ({
  caseById,
  caseType,
  caseStatus,
  categories,
  firstReport,
  firstMitigationPlan,
  formTitle,
  submissionRoute,
}: Props) => {
  const { tokens } = useTheme<LensTheme>();

  const [isApprovedWithoutMitigation, setIsApprovedWithoutMitigation] =
    useState(false);

  const { enableNamedOffenders, showCaseLinksSection } =
    useSelector(casePageHeader);

  useEffect(() => {
    const approvedWithoutMitigation =
      caseType === CaseTypeEnum.COI &&
      caseStatus === CoiCaseStatus.Closed &&
      firstMitigationPlan?.status === MitigationPlanStatus.NoApprovalRequired;
    setIsApprovedWithoutMitigation(approvedWithoutMitigation);
  }, [caseType, caseStatus, firstMitigationPlan]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.core.spacing[3].value,
        '&:first-of-type': {
          marginTop: tokens.core.spacing[4].value,
        },
      }}
    >
      {isApprovedWithoutMitigation && firstMitigationPlan && (
        <ApprovedWithoutMitigation {...firstMitigationPlan} />
      )}

      {submissionRoute === REPORT_SOURCE.imported && (
        <Alert severity="info">
          Imported case: This case was imported from a legacy system. Some
          fields have been mapped from the original data.
        </Alert>
      )}
      <Box
        sx={{
          backgroundColor: theme.atlas.semantic.color.surface.variant,
          borderRadius: tokens.semantic.radius.md.value,
          padding: 3,
          display: 'flex',
          gap: '24px',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '24px',
            width: '100%',
          }}
        >
          <CaseOverviewPanel
            label={caseById.name || formTitle}
            caseLead={caseById.CaseLead ?? null}
            categories={categories}
            status={caseStatus}
            submittedAt={firstReport?.submittedAt ?? new Date()}
            submissionRoute={submissionRoute}
            lastUpdated={caseById?.updatedAt ?? null}
            caseType={caseType}
          />
          <CaseSubjectDetails caseById={caseById} caseType={caseType} />
        </Box>
        {caseType === CaseTypeEnum.SPEAK_UP && showCaseLinksSection && (
          <LinkCasesSection />
        )}
      </Box>
      {caseType === CaseTypeEnum.SPEAK_UP && enableNamedOffenders && (
        <DqNamedOffendersSection
          reportId={firstReport?.id != null ? String(firstReport.id) : ''}
        />
      )}
      <RequiredFeatureEnabled requiredFeature={FeatureFlag.CUSTOM_FIELDS}>
        <CaseCustomFields caseId={caseById.id} />
      </RequiredFeatureEnabled>
      <Box
        sx={{
          backgroundColor: theme.atlas.semantic.color.surface.variant,
          borderRadius: tokens.semantic.radius.md.value,
          padding: '24px',
        }}
      >
        <CaseFormResponses caseById={caseById} caseType={caseType} />
      </Box>
    </Box>
  );
};
